import React, { useEffect } from 'react';
import { View, ActivityIndicator, StyleSheet } from 'react-native';
import { useRouter, useSegments } from 'expo-router';
import { AuthGuardError } from '@/components/AuthGuardError';
import { useAuth } from '@/lib/supabase';
import { useThemeColors } from '@/lib/useThemeColors';
import { deletionBackendAvailable, useDeletionStatus } from '@/lib/gdpr/account-deletion';
import { DELETION_PENDING_ROUTE, resolveDeletionGate } from '@/lib/gdpr/deletion-gate';

/**
 * Root-level pending-deletion gate (118-07). Mounted ONCE, beside the root
 * Stack in RootLayoutNav, so it sees every route — deep links and web refreshes
 * included. Decision in `src/lib/gdpr/deletion-gate.ts`; the answer comes from
 * the server's `status` action (AAL1), never from local cache.
 *
 * It does not wrap the navigator (RootLayoutNav must not be wrapped): it draws
 * an absolute overlay above it while the answer is outstanding or failed, and
 * keeps that overlay until a redirect has actually landed.
 */
export function DeletionGate() {
  const { user } = useAuth();
  const segments = useSegments();
  const router = useRouter();
  const colors = useThemeColors();
  const status = useDeletionStatus(user?.id);

  const decision = resolveDeletionGate({
    signedIn: !!user,
    backendAvailable: deletionBackendAvailable(),
    isError: status.isError,
    deletion: status.data,
    segment: segments[0],
  });

  useEffect(() => {
    if (decision === 'redirect') router.replace(DELETION_PENDING_ROUTE);
  }, [decision]); // eslint-disable-line react-hooks/exhaustive-deps

  if (decision === 'pass') return null;

  return (
    <View
      testID="deletion-gate-overlay"
      style={[StyleSheet.absoluteFill, { backgroundColor: colors.surface.primary, zIndex: 1000 }]}
    >
      {decision === 'error' ? (
        <AuthGuardError onRetry={() => status.refetch()} />
      ) : (
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <ActivityIndicator color={colors.primary[600]} />
        </View>
      )}
    </View>
  );
}
